import * as React from 'react';
import { Row } from 'react-bootstrap';
import { Field } from 'redux-form';

import { OperandType } from '@acceligentllc/shared/enums/operand';
import type { OperationType } from '@acceligentllc/shared/enums/operation';
import { MinOperandCount } from '@acceligentllc/shared/enums/operation';
import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';

import { bemElement } from 'af-utils/bem.util';
import * as ReduxUtils from 'af-utils/reduxForms.util';

import { Button } from '@acceligentllc/storybook';

import Dropdown from 'af-fields/Dropdown';
import Input from 'af-fields/Input';

import styles from './styles.module.scss';
import type { ReportBlockFieldFormModel, CalculatedFieldOptionFormModel } from '../formModel';
import { getOptionFieldFilter, getOptionLabel } from './helpers';

interface OwnProps {
	additionalFilter?: (option: ReportBlockFieldFormModel) => boolean;
	fieldName: string;
	fieldsByIdMap: { [fieldId: string]: ReportBlockFieldFormModel; };
	index: number;
	onChange: (index: number, value: CalculatedFieldOptionFormModel) => void;
	onRemove: (index: number) => void;
	operandCount: number;
	operation: Nullable<OperationType>;
	selectedFieldsMap: { [key: string]: number; };
	type: CalculatedFieldOptionFormModel['type'];
	unit: Nullable<QuantityUnitType>;
	options: CalculatedFieldOptionFormModel[];
}

type Props = OwnProps;

const _renderItem = (fieldName: string, label: string, blockName: Nullable<string>) => {
	return (
		<div className="report-block-form-field-modal__dropdown-option">
			{blockName && <div className={bemElement('report-block-form-field-modal__dropdown-option', 'item', ['block'])}>{blockName}</div>}
			<div className="report-block-form-field-modal__dropdown-option__item">{fieldName}</div>
			<div className={bemElement('report-block-form-field-modal__dropdown-option', 'item', ['meta'])}>
				{label}
			</div>
		</div>
	);
};

const _renderConstant = (fieldName: string) => {
	return (
		<Field
			component={Input}
			format={ReduxUtils.formatDecimalNumber}
			id={fieldName}
			isRequired={true}
			label="Constant"
			name={`${fieldName}.constant`}
			placeholder="Enter Constant Value"
			type="number"
		/>
	);
};

const CalculatedFieldOption: React.FC<Props> = (props: Props) => {
	const {
		operation,
		operandCount,
		fieldsByIdMap,
		unit,
		index,
		onRemove,
		onChange,
		fieldName,
		selectedFieldsMap,
		additionalFilter,
		type,
	} = props;

	const [
		selectableFields,
		setSelectableFields,
	] = React.useState<CalculatedFieldOptionFormModel[]>([]);

	const getFilterFunc = React.useCallback(() => {
		return getOptionFieldFilter(operation, unit, selectedFieldsMap, fieldsByIdMap, additionalFilter);
	}, [operation, unit, selectedFieldsMap, fieldsByIdMap, additionalFilter]);

	React.useEffect(() => {
		let newFields: CalculatedFieldOptionFormModel[] = [];
		const optionsFilter = getFilterFunc();
		newFields = props.options.reduce<CalculatedFieldOptionFormModel[]>((_acc, _field) => {
			if (optionsFilter(_field, props.index)) {
				_acc.push(_field);
			}
			return _acc;
		}, []);
		setSelectableFields(newFields);
	}, [props.options, props.index, getFilterFunc]);

	const renderOptionItem = React.useCallback((option: CalculatedFieldOptionFormModel) => {
		const field = option.id ? fieldsByIdMap[option.id] : null;
		return field
			? _renderItem(field.name, getOptionLabel(field), null)
			: null;
	}, [fieldsByIdMap]);

	const remove = React.useCallback(() => {
		onRemove(index);
	}, [index, onRemove]);

	const change = React.useCallback((value: CalculatedFieldOptionFormModel) => {
		onChange(index, { id: value.id, constant: null, type: OperandType.FIELD, index, isOperandBlockInPrimarySegment: value.isOperandBlockInPrimarySegment });
	}, [index, onChange]);

	const renderFieldDropdown = React.useCallback(() => {
		if (type === OperandType.CONSTANT) {
			return _renderConstant(fieldName);
		}
		return (
			<Field
				className="report-block-form-field-modal__calculation-dropdown"
				component={Dropdown}
				fixed={true}
				id={fieldName}
				label={`Field #${index + 1}`}
				name={fieldName}
				onValueChange={change}
				options={selectableFields}
				placeholder="Select"
				renderMenuItem={renderOptionItem}
				valueKey="id"
				withCaret={true}
			/>
		);
	}, [change, fieldName, index, renderOptionItem, selectableFields, type]);

	return (
		<div className={styles.container}>
			<Row className={styles.field}>
				{renderFieldDropdown()}
				{operation && operandCount > MinOperandCount[operation] &&
					<Button
						icon="Delete"
						onClick={remove}
						style="link-danger"
					/>
				}
			</Row>
		</div>
	);
};

export default React.memo(CalculatedFieldOption);
